import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Sparkles, Heart, Wheat, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { ProductCard } from "@/components/storefront/ProductCard";
import { useSettings } from "@/context/SettingsContext";

const values = [
  { icon: Leaf, title: "No Preservatives", desc: "Nothing artificial, ever. Just honest baking." },
  { icon: Sparkles, title: "Pure Ingredients", desc: "Quality ingredients you can trust." },
  { icon: Heart, title: "Made With Love", desc: "Handcrafted in small batches." },
  { icon: Wheat, title: "Freshly Baked", desc: "Baked fresh, delivered fresh." },
];

const Home = () => {
  const [best, setBest] = useState([]);
  const [cats, setCats] = useState([]);
  const [about, setAbout] = useState("");
  const [offers, setOffers] = useState([]);
  const { logo } = useSettings();

  useEffect(() => {
    api.get("/products", { params: { best_seller: true } }).then((r) => setBest(r.data.filter((x) => x.available).slice(0, 8)));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/settings").then((r) => setAbout(r.data.about)).catch(() => {});
    api.get("/offers/active").then((r) => setOffers(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/d19db0b9-4d6c-4a45-abc6-252ca7a69ad6/images/e092efd3c64a20b116dd087926cdaa9c61a409e34d3ecad4834e51bebb51487f.jpeg"
            alt="Nel Bake House"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-chocodark/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <img src={logo} alt="Nel Bake House" className="h-24 w-24 sm:h-28 sm:w-28 rounded-full object-cover ring-2 ring-gold/40 shadow-2xl mb-6" />
            <p className="text-gold text-xs sm:text-sm uppercase tracking-[0.35em] mb-4">Healthy & No Preservatives</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.05]">
              Freshly Baked.<br />Made With Love.
            </h1>
            <p className="text-cream/80 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              Premium handmade bakes crafted with quality ingredients and no preservatives — from chocolate cookie tins
              to fudgy brownies and crispy kunafa.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link
                to="/shop"
                data-testid="hero-shop-now"
                className="inline-flex items-center gap-2 rounded-full bg-gold text-chocodark font-semibold px-7 py-3.5 hover:bg-goldsoft active:scale-95 transition-[background-color,transform]"
              >
                Shop Now <ArrowRight size={18} />
              </Link>
              <Link
                to="/shop"
                data-testid="hero-explore-menu"
                className="inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream font-semibold px-7 py-3.5 hover:bg-cream/10 transition-colors"
              >
                Explore Menu
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ACTIVE OFFERS */}
      {offers.length > 0 && (
        <section data-testid="offers-section" className="bg-choco grain">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12 sm:py-16">
            <p className="text-gold text-xs uppercase tracking-[0.3em] mb-2">Limited time</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-cream mb-8">Current Offers</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {offers.map((o) => (
                <div key={o.id} data-testid={`offer-card-${o.id}`} className="rounded-2xl border border-gold/30 bg-cream/5 p-6">
                  <span className="inline-block rounded-full bg-gold text-chocodark text-sm font-bold px-3 py-1">{o.discount_percent}% OFF</span>
                  <h3 className="font-display text-xl font-bold text-cream mt-3">{o.title}</h3>
                  <p className="text-sm text-cream/75 mt-1.5">{o.description}</p>
                  <p className="mt-4 text-sm text-cream/80">
                    Use code <span className="font-mono font-bold tracking-widest text-gold">{o.coupon_code}</span> at checkout
                  </p>
                  {o.end_date && <p className="text-xs text-cream/50 mt-2">Valid till {o.end_date}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VALUES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-2xl p-6 sm:p-7 border border-[#EFEBE1] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]"
            >
              <div className="h-12 w-12 rounded-full bg-mutedcream flex items-center justify-center text-goldsoft">
                <v.icon size={24} />
              </div>
              <h3 className="font-display text-xl font-semibold text-choco mt-5">{v.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-4">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-goldsoft text-xs uppercase tracking-[0.3em] mb-2">Loved by all</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-choco">Best Sellers</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-choco hover:text-goldsoft transition-colors">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {best.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-center mb-10">
          <p className="text-goldsoft text-xs uppercase tracking-[0.3em] mb-2">Explore</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-choco">Shop by Category</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {cats.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
            >
              <Link
                to={`/shop?category=${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group relative block rounded-2xl overflow-hidden aspect-[4/3] bg-choco grain"
              >
                <div className="relative h-full flex flex-col justify-between p-6">
                  <span className="font-display text-5xl font-bold text-gold/20">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-cream">{c.name}</h3>
                    <span className="inline-flex items-center gap-1 text-gold text-sm mt-1 group-hover:gap-2 transition-[gap]">Shop now <ArrowRight size={14} /></span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-choco text-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-gold text-xs uppercase tracking-[0.3em] mb-3">Our Story</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-cream mb-6">Baked fresh, the honest way</h2>
            <p className="text-cream/75 leading-relaxed">{about}</p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-7 rounded-full bg-gold text-chocodark font-semibold px-6 py-3 hover:bg-goldsoft transition-colors">
              Learn more <ArrowRight size={16} />
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3]">
            <img src="https://static.prod-images.emergentagent.com/jobs/d19db0b9-4d6c-4a45-abc6-252ca7a69ad6/images/e092efd3c64a20b116dd087926cdaa9c61a409e34d3ecad4834e51bebb51487f.jpeg" alt="Bakery" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 text-center">
        <p className="text-goldsoft text-xs uppercase tracking-[0.3em] mb-2">Why Nel Bake House</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-choco mb-12">Made better, tastes better</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
          {["Healthy & No Preservatives", "Pure Ingredients", "Freshly Baked", "Made With Love"].map((t, i) => (
            <div key={t} className="p-4">
              <span className="font-display text-5xl font-bold text-gold">0{i + 1}</span>
              <p className="font-display text-lg font-semibold text-choco mt-3">{t}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
